import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["activeKey", "bordered", "children", "className", "commonTabContent", "disableTransition", "arrowClassName", "onSelectTab", "tabActiveBgColor", "tabActiveClassName", "tabBgColor", "tabClassName", "tabContentClassName", "tabFirstChildClassName", "tabHoverBgColor", "tabHoverClassName", "tabLastChildClassName", "tabsWrapperClassName", "vertical"];
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { AnimatePresence } from 'framer-motion';
var getFirstTabKey = function getFirstTabKey() {
  var children = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  var _children = _slicedToArray(children, 1),
    firstItem = _children[0];
  return firstItem === null || firstItem === void 0 ? void 0 : firstItem.props.tabKey;
};
var DataTabs = function DataTabs(props) {
  var activeKey = props.activeKey,
    bordered = props.bordered,
    children = props.children,
    wrapperClassName = props.className,
    commonTabContent = props.commonTabContent,
    disableTransition = props.disableTransition,
    arrowClassName = props.arrowClassName,
    onSelectTab = props.onSelectTab,
    tabActiveBgColor = props.tabActiveBgColor,
    tabActiveClassName = props.tabActiveClassName,
    tabBgColor = props.tabBgColor,
    tabClassName = props.tabClassName,
    tabContentClassName = props.tabContentClassName,
    tabFirstChildClassName = props.tabFirstChildClassName,
    tabHoverBgColor = props.tabHoverBgColor,
    tabHoverClassName = props.tabHoverClassName,
    tabLastChildClassName = props.tabLastChildClassName,
    tabsWrapperClassName = props.tabsWrapperClassName,
    vertical = props.vertical,
    remainingProps = _objectWithoutProperties(props, _excluded);
  var _useState = useState(activeKey || getFirstTabKey(children)),
    _useState2 = _slicedToArray(_useState, 2),
    internalActiveKey = _useState2[0],
    setInternalActiveKey = _useState2[1];
  useEffect(function () {
    activeKey && setInternalActiveKey(activeKey);
  }, [activeKey]);
  var handleSelectTab = function handleSelectTab(event) {
    onSelectTab(event.currentTarget.getAttribute('data-tabkey'));
  };
  var wrapperClassNames = classNames('DataTabsContainer', bordered && 'DataTab-bordered', wrapperClassName, vertical ? 'DataTabs-vertical' : 'DataTabs-horizontal');
  var enrichChildren = function enrichChildren() {
    if (disableTransition) {
      return React.Children.map(children, function (child) {
        return /*#__PURE__*/React.cloneElement(child, {
          active: child.props.tabKey === internalActiveKey,
          disableTransition: disableTransition
        });
      });
    }
    var enhancedChildren = React.Children.toArray(children).filter(function (child) {
      return child.props.tabKey === internalActiveKey;
    }).map(function (child) {
      return /*#__PURE__*/React.cloneElement(child, {
        active: true,
        disableTransition: disableTransition
      });
    });
    return /*#__PURE__*/React.createElement(AnimatePresence, {
      initial: false,
      exitBeforeEnter: true
    }, enhancedChildren);
  };
  return /*#__PURE__*/React.createElement("div", _extends({}, remainingProps, {
    className: wrapperClassNames
  }), /*#__PURE__*/React.createElement("div", {
    className: "DataTabsWrapper ".concat(tabsWrapperClassName)
  }, /*#__PURE__*/React.createElement("ul", {
    className: "DataTabs",
    role: "tabList"
  }, React.Children.map(children, function (child, index) {
    var _useState3 = useState(false),
      _useState4 = _slicedToArray(_useState3, 2),
      isHovering = _useState4[0],
      setIsHovering = _useState4[1];
    var _child$props = child.props,
      tabKey = _child$props.tabKey,
      title = _child$props.title,
      className = _child$props.className;
    var isActive = tabKey === internalActiveKey;
    var tabClassNames = classNames('DataTab', 'tab', index === 0 && tabFirstChildClassName, index === children.length - 1 && tabLastChildClassName, !isHovering && !isActive && tabClassName, isHovering && !isActive && tabHoverClassName, isActive && "active ".concat(tabActiveClassName), !isActive && 'cursor-pointer', className && className);
    return /*#__PURE__*/React.createElement("li", {
      key: tabKey,
      className: tabClassNames,
      onClick: handleSelectTab,
      "data-tabkey": tabKey,
      onMouseEnter: function onMouseEnter() {
        return setIsHovering(true);
      },
      onMouseLeave: function onMouseLeave() {
        return setIsHovering(false);
      }
    }, title, /*#__PURE__*/React.createElement("div", {
      className: "arrow ".concat(arrowClassName, " ").concat(isActive ? 'in' : '')
    }));
  }))), /*#__PURE__*/React.createElement("div", {
    className: "tab-content ".concat(tabContentClassName),
    role: "tabpanel"
  }, commonTabContent ? commonTabContent : enrichChildren()));
};
DataTabs.defaultProps = {
  activeKey: '',
  bordered: true,
  children: [],
  className: '',
  disableTransition: false,
  arrowClassName: 'bg-highlight-dark border-color-transparent',
  onSelectTab: function onSelectTab() {},
  tabActiveClassName: 'bg-highlight-dark text-color-white',
  tabClassName: '',
  tabContentClassName: '',
  tabFirstChildClassName: '',
  tabHoverClassName: 'bg-highlight-decent text-color-darkest',
  tabLastChildClassName: '',
  tabsWrapperClassName: '',
  vertical: false
};
DataTabs.propTypes = {
  activeKey: PropTypes.string,
  bordered: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
  className: PropTypes.string,
  commonTabContent: PropTypes.node,
  disableTransition: PropTypes.bool,
  arrowClassName: PropTypes.string,
  onSelectTab: PropTypes.func.isRequired,
  tabActiveClassName: PropTypes.string,
  tabClassName: PropTypes.string,
  tabContentClassName: PropTypes.string,
  tabFirstChildClassName: PropTypes.string,
  tabHoverClassName: PropTypes.string,
  tabLastChildClassName: PropTypes.string,
  tabsWrapperClassName: PropTypes.string,
  variant: PropTypes.string,
  vertical: PropTypes.bool
};
export default DataTabs;